import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  OutlinedInput,
  Switch,
  FormControlLabel,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Typography,
  IconButton,
  Box,
  Grid,
  ListItemText,
  CircularProgress
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import { GetSchoolLeadList, SchoolLeadStatus } from "../api/auth/authService";
import { useSnackbar } from "../components/SnackBar/SnackbarContext";

const moduleOptions = [
  { id: 1, name: "Admin" }, { id: 2, name: "Class Structure" },
  { id: 3, name: "Exam" }, { id: 4, name: "Fees" },
  { id: 5, name: "Payroll" }, { id: 6, name: "Student" },
  { id: 7, name: "Staff" }, { id: 8, name: "Transport" }
];
const supportMemberOptions = [{ id: "1", label: "Yash Nagla" }];
const designationOptions = [{ id: 1, name: "Principal" }, { id: 2, name: "Admin" }];
const CALL_TYPE_OPTIONS = ["Follow Up", "Hot", "Warm", "Cold", "Closed"];

const initialFormState = {
  SchoolID: "",
  CallStatusID: "",
  SchoolName: "",
  // SchoolBranchName: "",
  SchoolCity: "",
  Modules: [],
  OtherModules: "",
  CallDate: null,
  CallTime: null,
  EndTime: null,
  Name: "",
  Number: "",
  Email: "",
  SupportMemberID: "",
  Designation: "",
  CallType: "",
  NeedFollowup: false,
  FollowupDate: null,
  CallHistory: [],
  Remark: ""
};

export default function LeadStatusModal({ open, onClose, isEditMode, schoolOptions, selectedSchool }) {
  const { showSnackbar } = useSnackbar();
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    if (open && selectedSchool) {
      fetchSchoolLeadDetails(selectedSchool.id);
    } else if (!open) {
      setFormData(initialFormState);
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedSchool]);

  const fetchSchoolLeadDetails = async (schoolID) => {
    setLoadingDetails(true);
    try {
      const response = await GetSchoolLeadList({ schoolID, pageNumber: 1, pageSize: 20 });

      if (response.success && response.data) {
        const schoolInfo = response.data.schoolList?.[0] || {};
        const leadInfo = response.data.data?.[0] || {};

        const modulesArray = leadInfo.moduleNames
          ? leadInfo.moduleNames.split(",").map(m => m.trim())
          : [];

        const matchedSupportMember = supportMemberOptions.find(
          (s) => s.label === leadInfo.supportMemberName
        );

        const callDateStr = leadInfo.callDate ? leadInfo.callDate.split("T")[0] : "";

        setFormData((prev) => ({
          ...prev,
          SchoolID: schoolInfo.schoolID || selectedSchool.id || "",
          CallStatusID: leadInfo.callStatusID || leadInfo.id || "",
          SchoolName: schoolInfo.schoolName || selectedSchool.label || "",
          // SchoolBranchName: schoolInfo.schoolBranchName || "",
          SchoolCity: schoolInfo.schoolCity || "",
          Modules: modulesArray,
          OtherModules: leadInfo.otherModules || "",

          CallDate: leadInfo.callDate ? dayjs(leadInfo.callDate) : null,
          CallTime: callDateStr && leadInfo.callTime ? dayjs(`${callDateStr}T${leadInfo.callTime}`) : null,
          EndTime: callDateStr && leadInfo.callEndTime ? dayjs(`${callDateStr}T${leadInfo.callEndTime}`) : null,

          Name: leadInfo.contactPersonName || "",
          Number: leadInfo.contactNumber || "",
          Email: leadInfo.schoolEmail || schoolInfo.schoolEmail || "",
          SupportMemberID: matchedSupportMember ? matchedSupportMember.id : "",
          Designation: leadInfo.designation || "",
          CallType: leadInfo.leadStatus || "",
          NeedFollowup: leadInfo.needFollowup || false,
          FollowupDate: leadInfo.followupDate ? dayjs(leadInfo.followupDate) : null,
          Remark: leadInfo.remark || "",
          CallHistory: leadInfo.callHistory || []
        }));
      }
    } catch (error) {
      showSnackbar("Failed to fetch lead details", "error");
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (name, val) => {
    setFormData((prev) => ({ ...prev, [name]: val }));
  };

  const handleModulesChange = (event) => {
    const { target: { value } } = event;
    setFormData((prev) => ({ ...prev, Modules: typeof value === 'string' ? value.split(',') : value }));
  };

  const handleFollowupToggle = (e) => {
    setFormData((prev) => ({ ...prev, NeedFollowup: e.target.checked }));
  };

  const handleSubmit = async () => {
    setLoadingDetails(true);
    try {
      const payload = new FormData();
      if (isEditMode && formData.CallStatusID) {
        payload.append("CallStatusID", formData.CallStatusID);
      }

      payload.append("SchoolName", formData.SchoolName);
      payload.append("SchoolEmail", formData.Email);
      // payload.append("SchoolBranchName", formData.SchoolBranchName);
      payload.append("SchoolCity", formData.SchoolCity);
      if (formData.SchoolID) payload.append("SchoolID", formData.SchoolID);
      if (formData.CallDate) payload.append("CallDate", dayjs(formData.CallDate).format("YYYY-MM-DDTHH:mm:ss"));
      if (formData.CallTime) payload.append("CallTime", dayjs(formData.CallTime).format("HH:mm:ss"));
      payload.append("CallEndTime", formData.EndTime ? dayjs(formData.EndTime).format("HH:mm:ss") : "");
      payload.append("LeadStatus", formData.CallType);
      payload.append("ContactNumber", formData.Number);
      payload.append("ContactPersonName", formData.Name);
      payload.append("Designation", formData.Designation);
      payload.append("Remark", formData.Remark);
      payload.append("NeedFollowup", formData.NeedFollowup);
      payload.append("FollowupDate", formData.NeedFollowup && formData.FollowupDate ? dayjs(formData.FollowupDate).format("YYYY-MM-DDTHH:mm:ss") : "");
      payload.append("ModifiedBy", formData.SupportMemberID);
      payload.append("SupportMemberID", formData.SupportMemberID);

      const moduleIDs = formData.Modules
        .map((name) => moduleOptions.find((module) => module.name === name)?.id)
        .filter(Boolean)
        .join(",");
      payload.append("ModuleIDs", moduleIDs);
      payload.append("OtherModules", isOtherModulesSelected(formData.Modules) ? formData.OtherModules : "");

      const response = await SchoolLeadStatus(payload);
      if (response?.success) {
        showSnackbar(response.message || (isEditMode ? "Lead status updated successfully." : "Lead status saved successfully."), "success");
        onClose();
      } else {
        showSnackbar(response?.message || "Failed to save lead status.", "error");
      }
    } catch (error) {
      showSnackbar(error?.response?.data?.message || "Something went wrong.", "error");
    } finally {
      setLoadingDetails(false);
    }
  };

  const isOtherModulesSelected = (modules) => modules?.includes("Other");
  const renderModulesValue = (selected) => selected.join(", ");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", bgcolor: "primary.main", color: "white" }}>
        <Typography variant="h6">{isEditMode ? "Edit Lead Status" : "Add Lead Status"}</Typography>
        <IconButton onClick={onClose} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 3 }}>
        {loadingDetails ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box component="form" noValidate>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2}>

                <Grid size={{ xs: 12, sm: 4 }}>
                  <Autocomplete
                    freeSolo
                    className={`cp-autocomplete-clean ${errors.SchoolName ? "error" : ""}`}
                    size="small"
                    options={schoolOptions}
                    filterOptions={(options, state) => {
                      const query = state.inputValue.trim().toLowerCase();
                      if (!query) return options;
                      return options.filter((o) => (o.schoolName || o.label || "").toLowerCase().includes(query));
                    }}
                    getOptionLabel={(option) => typeof option === "string" ? option : option.schoolName || option.label || ""}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    inputValue={formData.SchoolName || ""}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "input") {
                        setFormData((prev) => ({ ...prev, SchoolName: newInputValue, SchoolID: "" }));
                        if (errors.SchoolName) setErrors((prev) => ({ ...prev, SchoolName: undefined }));
                      }
                    }}
                    onChange={(event, newValue) => {
                      if (typeof newValue === "string") {
                        setFormData((prev) => ({ ...prev, SchoolName: newValue, SchoolID: "" }));
                      } else if (newValue && typeof newValue === "object") {
                        fetchSchoolLeadDetails(newValue.id || newValue.schoolID);
                      } else {
                        setFormData((prev) => ({ ...prev, SchoolID: "", SchoolName: "" }));
                      }
                      if (errors.SchoolName) setErrors((prev) => ({ ...prev, SchoolName: undefined }));
                    }}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Typography variant="body2" fontWeight={600}>
                          {option.schoolName || option.label}
                        </Typography>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label="School Name" variant="filled" size="small" fullWidth required />
                    )}
                  />
                </Grid>
                {/* <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField size="small" fullWidth label="Branch Name" name="SchoolBranchName" value={formData.SchoolBranchName} onChange={handleChange} variant="filled" className="cp-form-control input-focus" />
                </Grid> */}
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField size="small" fullWidth label="City" name="SchoolCity" value={formData.SchoolCity} onChange={handleChange} variant="filled" className="cp-form-control input-focus" />
                </Grid>

                <Grid size={{ xs: 12, sm: 4 }}>
                  <FormControl fullWidth size="small" variant="filled" error={!!errors.Modules} className={`cp-form-control input-focus ${errors.Modules ? "error" : ""}`}>
                    <InputLabel>Request Modules</InputLabel>
                    <Select multiple value={formData.Modules} onChange={handleModulesChange} input={<OutlinedInput label="Request Modules" />} renderValue={renderModulesValue} sx={{ "& .MuiSelect-select": { display: "flex", alignItems: "center", py: "12px" } }} MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}>
                      {moduleOptions.map((mod) => (
                        <MenuItem key={mod.id} value={mod.name}>
                          <Checkbox checked={(formData.Modules || []).includes(mod.name)} />
                          <ListItemText primary={mod.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {isOtherModulesSelected(formData.Modules) && (
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField fullWidth size="small" variant="outlined" label="Other Modules" name="OtherModules" value={formData.OtherModules} onChange={handleChange} className="cp-form-control input-focus" />
                  </Grid>
                )}
                <Grid size={{ xs: 12, sm: 4 }}>
                  <DatePicker className="cp-form-control input-focus" label=" Date" value={formData.CallDate} format="DD/MM/YYYY" onChange={(val) => handleDateChange("CallDate", val)} slotProps={{ textField: { variant: "filled", size: "small", fullWidth: true, required: true } }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TimePicker className="cp-form-control input-focus" label="Call Start Time" value={formData.CallTime} onChange={(val) => handleDateChange("CallTime", val)} slotProps={{ textField: { variant: "filled", size: "small", fullWidth: true, required: true } }} />
                </Grid>

                <Grid size={{ xs: 12, sm: 4 }}>
                  <TimePicker className="cp-form-control input-focus" label="Call End Time" value={formData.EndTime} onChange={(val) => handleDateChange("EndTime", val)} slotProps={{ textField: { variant: "filled", size: "small", fullWidth: true, required: true } }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField size="small" fullWidth label="Client Name" name="Name" value={formData.Name} onChange={handleChange} variant="filled" className={`cp-form-control input-focus ${errors.Name ? "error" : ""}`} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField size="small" fullWidth label="Client Number" name="Number" value={formData.Number} variant="filled" required className="cp-form-control input-focus" inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }} onChange={(e) => { const onlyNumbers = e.target.value.replace(/[^0-9]/g, ""); handleChange({ target: { name: "Number", value: onlyNumbers.slice(0, 10) } }); }} />
                </Grid>

                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField size="small" fullWidth label="Client Email" name="Email" value={formData.Email} onChange={handleChange} variant="filled" className="cp-form-control input-focus" />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FormControl fullWidth size="small" variant="filled" required>
                    <InputLabel>Support Member Name</InputLabel>
                    <Select name="SupportMemberID" value={formData.SupportMemberID} onChange={handleChange}>
                      {supportMemberOptions.map((member) => (
                        <MenuItem key={member.id} value={member.id}>{member.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FormControl fullWidth size="small" variant="filled" required>
                    <InputLabel>Client Designation</InputLabel>
                    <Select name="Designation" value={formData.Designation} onChange={handleChange}>
                      {designationOptions.map((item) => <MenuItem key={item.id} value={item.name}>{item.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 4 }}>
                  <FormControl fullWidth size="small" variant="filled" required error={!!errors.CallType}>
                    <InputLabel>Lead Status</InputLabel>
                    <Select name="CallType" value={formData.CallType} onChange={handleChange}>
                      {CALL_TYPE_OPTIONS.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12 }} display="flex" alignItems="center" gap={2}>
                  <FormControlLabel control={<Switch checked={formData.NeedFollowup} onChange={handleFollowupToggle} color="primary" />} label="Need to Follow Up" />
                  {formData.NeedFollowup && (
                    <DatePicker format="DD/MM/YYYY" label="Follow Up Date" value={formData.FollowupDate} onChange={(val) => handleDateChange("FollowupDate", val)} slotProps={{ textField: { variant: "filled", size: "small", sx: { minWidth: '200px' } } }} />
                  )}
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField size="small" fullWidth multiline minRows={3} label="Remark" name="Remark" value={formData.Remark} onChange={handleChange} variant="filled" className="cp-form-control input-focus" />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TableContainer sx={{ boxShadow: "none", border: "1px solid #d7d0d0", overflow: "auto" }}>
                    <Table stickyHeader aria-label="call history">
                      <TableHead>
                        <TableRow>
                          {[
                            ["S.No.", 0], ["Client Name", 120], ["Client Number", 110],
                            ["Client Email", 160], ["Client Designation", 120], ["Call Date", 100],
                            ["Call Start Time", 100], ["Call End Time", 100], ["Remark", 250]
                          ].map(([label, minWidth]) => (
                            <TableCell key={label} sx={{ minWidth, padding: "8px", backgroundColor: "#f5f5f5", fontSize: "13px" }}>
                              <strong>{label}</strong>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formData.CallHistory && formData.CallHistory.length > 0 ? (
                          formData.CallHistory.map((historyItem, index) => (
                            <TableRow key={index} hover>
                              <TableCell sx={{ padding: "8px", fontSize: "13px" }}>{index + 1}</TableCell>
                              <TableCell sx={{ padding: "8px", fontSize: "13px" }}>{historyItem.contactPersonName || "N/A"}</TableCell>
                              <TableCell sx={{ padding: "8px", fontSize: "13px" }}>{historyItem.contactNumber || "N/A"}</TableCell>
                              <TableCell sx={{ padding: "8px", fontSize: "13px" }}>{formData.Email || "N/A"}</TableCell>
                              <TableCell sx={{ padding: "8px", fontSize: "13px" }}>{historyItem.designation || "N/A"}</TableCell>
                              <TableCell sx={{ padding: "8px", fontSize: "13px" }}>{historyItem.callDate ? dayjs(historyItem.callDate).format("DD/MM/YYYY") : "N/A"}</TableCell>
                              <TableCell sx={{ padding: "8px", fontSize: "13px" }}>{historyItem.callTime ? dayjs(`2000-01-01T${historyItem.callTime}`).format("hh:mm A") : "N/A"}</TableCell>
                              <TableCell sx={{ padding: "8px", fontSize: "13px" }}>{historyItem.callEndTime ? dayjs(`2000-01-01T${historyItem.callEndTime}`).format("hh:mm A") : "N/A"}</TableCell>
                              <TableCell sx={{ minWidth: 250, padding: "8px", fontSize: "13px" }}>{historyItem.remark || "N/A"}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={9} align="center" sx={{ padding: "16px", fontSize: "13px" }}>No Call History Available</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

              </Grid>
            </LocalizationProvider>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleSubmit} variant="contained" color="primary" sx={{ boxShadow: "none" }} disabled={loadingDetails}>
          {isEditMode ? "Update Lead Status" : "Save Lead Status"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}